import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { services } from "../services";
import AdminLayout from "../layouts/AdminLayout";

const COURSE_COMPLETION_SAMPLE_FILE_URL = `https://${process.env.REACT_APP_AWS_BUCKET_NAME}.s3.ap-south-1.amazonaws.com/sampleFileCourceCompletion.xlsx`;

type DumpHistoryItem = {
  id: string;
  fileName: string;
  uploadDate: string;
  processingStatus: string;
  total: number | null;
  created: number | null;
  updated: number | null;
  duplicate: number | null;
  failed: number | null;
  processedAt: string | null;
  resultFileAvailable: boolean;
};

export function UpdateUserCourseCompletionContent() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [headerValidationError, setHeaderValidationError] = useState<
    string | null
  >(null);
  const [isHeaderValid, setIsHeaderValid] = useState(false);
  const [lastDumpRequestId, setLastDumpRequestId] = useState<string | null>(
    null,
  );
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const [summary, setSummary] = useState<{
    total?: number;
    created?: number;
    updated?: number;
    duplicate?: number;
    failed?: number;
    failedRows?: number[];
    errorMessage?: string | null;
  } | null>(null);
  const [uploadHistory, setUploadHistory] = useState<DumpHistoryItem[]>([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const pollerRef = useRef<number | null>(null);
  const pollAttemptRef = useRef(0);
  const pollStartRef = useRef<number | null>(null);
  const maxFileSizeMb = 25;
  const pollIntervalMs = 3000;
  const maxPollAttempts = 240;
  const maxPollDurationMs = 15 * 60 * 1000;

  const formatProcessingStatus = useCallback((status: string) => {
    switch (status) {
      case "success":
        return "Success";
      case "partial":
        return "Partial";
      case "failed":
        return "Failed";
      case "processing":
        return "Processing";
      case "pending":
        return "Pending";
      default:
        return status || "Unknown";
    }
  }, []);

  const fetchUploadHistory = useCallback(async () => {
    setIsHistoryLoading(true);
    try {
      const records = await services.requestSvc.get<DumpHistoryItem[]>(
        "/dump-status?type=course_completion",
        { withCredentials: true },
      );
      setUploadHistory(records || []);
    } catch (error) {
      console.error("Failed to fetch upload history", error);
      setUploadHistory([]);
    } finally {
      setIsHistoryLoading(false);
    }
  }, []);

  const requiredHeaders = useMemo(
    () => [
      "firstName",
      "lastName",
      "emailAddress/phoneNumber",
      "gender",
      "state",
      "district",
      "schoolName",
      "courseName",
      "completionStatus",
      "courseEndDatetime",
      "completion%",
    ],
    [],
  );

  const headerAliases = useMemo<Record<string, string>>(
    () => ({
      firstname: "firstName",
      lastname: "lastName",
      "emailaddress/phonenumber": "emailAddress/phoneNumber",
      gender: "gender",
      state: "state",
      district: "district",
      schoolname: "schoolName",
      coursename: "courseName",
      completionstatus: "completionStatus",
      courseenddatetime: "courseEndDatetime",
      "completion%": "completion%",
    }),
    [],
  );

  const normalizeHeader = useCallback(
    (value: string) => value.trim().toLowerCase().replace(/\s+/g, " "),
    [],
  );

  const validateHeaders = useCallback(
    (headers: string[]) => {
      const normalized = headers.map((header) => {
        const key = normalizeHeader(header);
        return headerAliases[key] || key;
      });
      const missing = requiredHeaders.filter(
        (header) => !normalized.includes(header),
      );
      return missing;
    },
    [normalizeHeader, requiredHeaders],
  );

  const clearPolling = useCallback(() => {
    if (pollerRef.current) {
      window.clearInterval(pollerRef.current);
      pollerRef.current = null;
    }
  }, []);

  useEffect(() => clearPolling, [clearPolling]);

  useEffect(() => {
    void fetchUploadHistory();
  }, [fetchUploadHistory]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(null);
    setIsHeaderValid(false);
    setHeaderValidationError(null);
    setSummary(null);
    setUploadStatus(null);
    clearPolling();

    if (file) {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!extension || !["csv", "xlsx"].includes(extension)) {
        setHeaderValidationError(
          "Invalid file type. Only CSV or XLSX allowed.",
        );
        return;
      }

      setSelectedFile(file);
      setUploadStatus("Validating headers...");

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const data = new Uint8Array(reader.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            range: 0,
            blankrows: false,
          }) as string[][];

          const headerRow = rows?.[0] || [];
          const missing = validateHeaders(
            headerRow.map((h) => String(h || "")),
          );

          if (missing.length > 0) {
            setHeaderValidationError(
              `Missing required headers: ${missing.join(", ")}`,
            );
            setIsHeaderValid(false);
          } else {
            setHeaderValidationError(null);
            setIsHeaderValid(true);
          }
        } catch (error) {
          setHeaderValidationError("Failed to read headers.");
          setIsHeaderValid(false);
        } finally {
          setUploadStatus(null);
        }
      };
      reader.onerror = () => {
        setHeaderValidationError("Failed to read headers.");
        setIsHeaderValid(false);
        setUploadStatus(null);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const pollStatus = useCallback(
    async (id: string) => {
      try {
        pollAttemptRef.current += 1;
        if (!pollStartRef.current) pollStartRef.current = Date.now();
        const elapsedMs = Date.now() - pollStartRef.current;
        if (
          pollAttemptRef.current > maxPollAttempts ||
          elapsedMs > maxPollDurationMs
        ) {
          clearPolling();
          setPollingTimedOut(true);
          setUploadStatus(
            "Processing is taking longer than expected. You can retry status or check later.",
          );
          setIsUploading(false);
          return;
        }

        const status = await services.requestSvc.get<any>(
          `/dump-status/${id}`,
          {
            withCredentials: true,
          },
        );

        if (
          status.processingStatus === "success" ||
          status.processingStatus === "partial"
        ) {
          clearPolling();
          setPollingTimedOut(false);
          setSummary({
            total: status.total,
            created: status.created,
            updated: status.updated,
            duplicate: status.duplicate,
            failed: status.failed,
            failedRows: status.failedRows || [],
          });
          setUploadStatus("Processing completed.");
          setIsUploading(false);
          void fetchUploadHistory();
        } else if (status.processingStatus === "failed") {
          clearPolling();
          setPollingTimedOut(false);
          setSummary({
            errorMessage: status.errorMessage,
            total: status.total,
            created: status.created,
            updated: status.updated,
            duplicate: status.duplicate,
            failed: status.failed,
            failedRows: status.failedRows || [],
          });
          setUploadStatus(status.errorMessage || "Processing failed.");
          setIsUploading(false);
          void fetchUploadHistory();
        }
      } catch (error) {
        setUploadStatus("Error fetching processing status.");
        clearPolling();
        setIsUploading(false);
      }
    },
    [clearPolling, maxPollAttempts, maxPollDurationMs, fetchUploadHistory],
  );

  const startPolling = useCallback(
    (id: string) => {
      clearPolling();
      pollAttemptRef.current = 0;
      pollStartRef.current = Date.now();
      setPollingTimedOut(false);
      pollerRef.current = window.setInterval(
        () => pollStatus(id),
        pollIntervalMs,
      );
      void pollStatus(id);
    },
    [clearPolling, pollStatus, pollIntervalMs],
  );

  const handleSubmit = async () => {
    setIsUploading(true);
    setSummary(null);
    if (selectedFile && isHeaderValid) {
      if (selectedFile.size > maxFileSizeMb * 1024 * 1024) {
        setUploadStatus(`File too large. Max size is ${maxFileSizeMb} MB.`);
        setIsUploading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append("file", selectedFile);

        const response = await services.requestSvc.post<{
          dumpRequestId: string;
        }>("/upload", formData, {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        const dumpRequestId = response.dumpRequestId;
        setLastDumpRequestId(dumpRequestId || null);
        setUploadStatus("File uploaded. Processing started...");
        void fetchUploadHistory();
        if (dumpRequestId) {
          startPolling(String(dumpRequestId));
        } else {
          setUploadStatus("Upload completed. Awaiting processing status.");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setUploadStatus("Error uploading file. Please try again.");
        setIsUploading(false);
      } finally {
        if (!pollerRef.current) setIsUploading(false);
      }
    } else {
      setUploadStatus(
        !selectedFile
          ? "No file selected"
          : "Header validation failed. Please fix the file headers.",
      );
      setIsUploading(false);
    }
  };

  const handleRetryStatus = () => {
    if (!lastDumpRequestId) return;
    setUploadStatus("Retrying status...");
    setIsUploading(true);
    setSummary(null);
    startPolling(lastDumpRequestId);
  };

  const handleDownload = async (id: string) => {
    try {
      const response = await services.requestSvc.get<{ url: string | null }>(
        `/dump-status/${id}/download`,
        { withCredentials: true },
      );
      if (response?.url) {
        window.open(response.url, "_blank", "noopener,noreferrer");
      } else {
        setUploadStatus("Result file not available yet.");
      }
    } catch (error) {
      console.error("Failed to download result file", error);
      setUploadStatus("Failed to download result file.");
    }
  };

  return (
    <>
      <div className="max-w-xl bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
        <h1 className="text-2xl font-bold mb-6 text-center">
          Upload Learner Status File
        </h1>
        <p className="text-sm text-gray-600 mb-4">
          Please download the sample file and upload the data in this format.
        </p>
        <a
          href={COURSE_COMPLETION_SAMPLE_FILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary w-full mb-4 inline-flex items-center justify-center"
        >
          Download sample file
        </a>
        <p className="text-sm text-gray-600 mb-4">
          Date format must be MM/DD/YYYY (or MM/DD/YYYY HH:mm:ss).
        </p>
        <p className="text-sm text-gray-600 mb-4">
          Optional headers: <code>placementStatus</code> and{" "}
          <code>placementType</code>. Use <code>placed</code> or{" "}
          <code>not placed</code> for placement status, and <code>Self</code>{" "}
          or <code>PP</code> for placement type.
        </p>
        <input
          type="file"
          className="border border-gray-300 p-2 rounded w-full mb-4"
          accept=".csv, .xlsx"
          onChange={handleFileChange}
          disabled={isUploading}
        />
        <button
          onClick={handleSubmit}
          className="btn-primary w-full"
          disabled={isUploading || !selectedFile || !isHeaderValid}
        >
          {isUploading ? "Uploading..." : "Submit"}
        </button>
        {headerValidationError && (
          <p className="text-red-500 mt-3">{headerValidationError}</p>
        )}
        {uploadStatus && (
          <p
            className={`text-${
              uploadStatus.toLowerCase().includes("error") ||
              uploadStatus.toLowerCase().includes("too large")
                ? "red"
                : "green"
            }-500`}
          >
            {uploadStatus}
          </p>
        )}
        {pollingTimedOut && lastDumpRequestId && (
          <button
            type="button"
            onClick={handleRetryStatus}
            className="btn-secondary w-full mt-3"
            disabled={isUploading}
          >
            Retry status
          </button>
        )}
      </div>
      <div className="mt-8 bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Upload History</h2>
          <button
            type="button"
            className="btn-secondary"
            onClick={fetchUploadHistory}
            disabled={isHistoryLoading}
          >
            {isHistoryLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        {isHistoryLoading ? (
          <div className="text-sm text-gray-500">Loading upload history...</div>
        ) : uploadHistory.length === 0 ? (
          <div className="text-sm text-gray-500">No uploads found yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-3 py-2 border-b">File Name</th>
                  <th className="text-left px-3 py-2 border-b">Uploaded At</th>
                  <th className="text-left px-3 py-2 border-b">Total</th>
                  <th className="text-left px-3 py-2 border-b">Created</th>
                  <th className="text-left px-3 py-2 border-b">Duplicate</th>
                  <th className="text-left px-3 py-2 border-b">Failed</th>
                  <th className="text-left px-3 py-2 border-b">Status</th>
                  <th className="text-left px-3 py-2 border-b">Action</th>
                </tr>
              </thead>
              <tbody>
                {uploadHistory.map((item) => (
                  <tr key={item.id} className="border-b last:border-b-0">
                    <td className="px-3 py-2">{item.fileName}</td>
                    <td className="px-3 py-2">
                      {item.uploadDate
                        ? new Date(item.uploadDate).toLocaleString()
                        : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {(item.total ?? item.total === 0) ? item.total : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {(item.created ?? item.created === 0)
                        ? item.created
                        : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {(item.duplicate ?? item.duplicate === 0)
                        ? item.duplicate
                        : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {(item.failed ?? item.failed === 0) ? item.failed : "-"}
                    </td>
                    <td className="px-3 py-2">
                      {formatProcessingStatus(item.processingStatus)}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => handleDownload(item.id)}
                        disabled={!item.resultFileAvailable}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default function UpdateUserCourseCompletionPage() {
  return (
    <AdminLayout title="Learner Status Updation">
      <UpdateUserCourseCompletionContent />
    </AdminLayout>
  );
}
